import { computed, ref, watch } from 'vue';
import { useStorage, watchDeep } from '@vueuse/core';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import type { AdditionalFee, AdoptionFee, RtoFee } from '../../../../types';
import type { FieldInitialSettings, FieldSchema, RawField, FileUpload, Field } from '@FormModule';
import type { GenericField, OId } from '@Types';
import type { Moment } from 'moment';
import { useAToast } from '@Atoms';
import { getShelterNow } from '@Constants';
import { useSessionStore } from '@Stores';
import { useForm, base64ToFile, fileToBase64 } from '@FormModule';
import {
  AddDiscountItemService,
  DeleteItemDiscount,
  RemoveOutcomeTempFileService,
  UpdateAdditionalFeesService,
  UpdateAnimalCartAdoptionFee,
  UpdateAnimalCartEuthanasiaFeeService,
  UpdateRtoFeeService,
  UploadOutcomeTempFileService,
} from '../../../../services';
import useCartStore from '../../../../store';
import { OUTCOMES_TYPES } from '../../../../types';
import getSanitizedPayload from './getSanitizedPayload';
import useFieldsOutcomeDate from './useFieldsOutcomeDate';

function getFeeIdString(id: OId | string | undefined): string {
  if (!id) return '';
  if (typeof id === 'string') return id;
  return id.$oid || '';
}

function matchesFeeId(feeId: OId | string | undefined, targetId: string): boolean {
  if (!feeId || !targetId) return false;
  if (typeof feeId === 'string') return feeId === targetId;
  return feeId.$oid === targetId;
}

export default function () {
  const { shelterProfile } = storeToRefs(useSessionStore());
  const {
    animals,
    cartData,
    discounts,
    hasCart,
    hasWaivers,
    isAdoption,
    isIntake,
    isEuthanasia,
    isReturnToOwner,
    outcomeInfoActiveStep,
    outcomeType,
    rtoFees,
    selectedRtoFee,
  } = storeToRefs(useCartStore());

  const { fieldsSchema: outcomeDateFieldSchema } = useFieldsOutcomeDate();

  const { notify, TOAST_TYPES } = useAToast();
  const { getDetailsSchema } = useForm();

  const NOT_ALLOWED_FIELD = ['outcome_type'];
  const TOAST_ERROR = {
    message: 'cart.toast.outcomeInfoUpdate.error.message',
    title: 'cart.toast.outcomeInfoUpdate.error.title',
    type: TOAST_TYPES.ERROR,
  };

  const fieldsSettings: FieldInitialSettings = {
    additional_fees: {
      model: [],
      edit: {
        label: 'label',
        options: [],
      },
      settings: {
        maxSelectedLabels: 1,
        disableFilterAutofocus: true,
      },
    },
    adoption_fee: {
      model: '',
      edit: {
        value: '_id.$oid',
        label: 'label',
        options: [],
      },
      settings: {
        show: true,
      },
    },
    discount: {
      edit: {
        label: 'code',
        options: discounts.value,
      },
      settings: {
        showClear: true,
      },
    },
    euthanasia_fee: {
      model: '',
      edit: {
        value: '_id',
        label: 'label',
        options: [],
      },
      settings: {
        show: true,
      },
    },
    file: {
      settings: {
        imageOnly: false,
        multiple: true,
        fileLimit: 5,
      },
    },
    partner_name: {
      edit: {
        label: 'name',
        value: 'id',
        options: [],
      },
    },
    rto_fee_type: {
      edit: {
        label: 'label',
        value: 'value',
        options: [
          {
            label: 'Daily',
            value: 'daily',
          },
          {
            label: 'Flat',
            value: 'flat',
          },
        ],
      },
    },
    rto_fees: {
      model: '',
      edit: {
        label: 'label',
        options: [''],
      },
      settings: {
        placeholder: 'Select a Fee Type first',
      },
      state: {
        disabled: true,
        errors: new Set(),
      },
    },
    waivers: {
      model: [],
      edit: {
        label: 'title',
        options: [],
      },
      settings: {
        maxSelectedLabels: 1,
        show: shelterProfile.value?.enable_outcome_waivers,
      },
    },
  };

  const uploadedFiles = useStorage<FileUpload>('cart-uploaded-files', {});
  const alreadyUploadedFiles = useStorage<Set<string>>('cart-files-ids', new Set());

  const additionalFeesOptions = ref();
  const adoptionFeesOptions = ref<AdoptionFee[]>();
  const currentDataOnStepperId = ref('');
  const currentSchemaModels = ref<Set<GenericField>>(new Set());
  const euthanasiaFeesOptions = ref();
  const fieldsSchema = ref<FieldSchema[]>([]);
  const formError = ref();
  const isStepChanging = ref(false);
  const outcomeSchemaModels = ref<any[]>([]);
  const rawFields = ref<RawField[]>([]);
  const sourceData = ref<any | null>(null);
  const waivers = ref([]);

  const additionalFeesModel = computed(() => fieldsSchema.value.find(({ name }) => name === 'additional_fees')?.model);
  const adoptionFee = computed(() => fieldsSchema.value.find(({ name }) => name === 'adoption_fee')?.model);
  const discountField = computed(() => fieldsSchema.value.find(({ name }) => name === 'discount')?.model);
  const euthanasiaFee = computed(() => fieldsSchema.value.find(({ name }) => name === 'euthanasia_fee')?.model);
  const filesModel = computed(() => fieldsSchema.value.find(({ settings }) => settings.type === 'file')?.model || []);
  const formData = computed(() => animals.value.find((data: any) => data._id === currentDataOnStepperId.value));
  const rtoFeeField = computed(() => fieldsSchema.value.find(({ name }) => name === 'rto_fees')?.model);
  const rtoFeeTypeField = computed(() => fieldsSchema.value.find(({ name }) => name === 'rto_fee_type')?.model);
  const waiversModel = computed(() => fieldsSchema.value.find(({ name }) => name === 'waivers')?.model);

  const additionalFeesBlurState = computed(
    () => fieldsSchema.value.find(({ name }) => name === 'additional_fees')?.state.blur
  );

  const additionalFeesWatchSource = computed(() => ({
    model: additionalFeesModel.value,
    blur: additionalFeesBlurState.value ?? false,
  }));

  const outcomeDateModel = computed(
    () => outcomeDateFieldSchema.value.find(({ name }) => name === 'outcome_date')?.model
  );

  watch(adoptionFee, onAdoptionFeeUpdate);
  watch(adoptionFeesOptions, (newValue, oldValue) => setNewSpeciesAdoptionFeeOptions(newValue, oldValue));
  watch(currentDataOnStepperId, () => onStepUpdate());
  watch(discountField, (newDiscount, oldDiscount) => onDiscountUpdate(newDiscount, oldDiscount));
  watch(euthanasiaFee, onEuthanasiaFeeUpdate);
  watch(euthanasiaFeesOptions, (newValue, oldValue) => setNewSpeciesEuthanasiaFeeOptions(newValue, oldValue));
  watch(filesModel, (newValue, oldValue) => onFilesModelUpdate(newValue, oldValue));
  watch(formData, () => setAdditionalFeeField(additionalFeesOptions.value));
  watch(outcomeDateModel, () => onOutcomeDateUpdate());
  watch(rtoFeeField, () => [onRtoFeesUpdate(), onOutcomeDateUpdate()]);
  watch(rtoFeeTypeField, () => onRtoFeeTypeUpdate());
  watchDeep(additionalFeesWatchSource, (newValue, oldValue) => handleAdditionalFeesChange(newValue, oldValue));
  watchDeep(waiversModel, onWaiversUpdate);

  // SETS
  function setForm() {
    if (!sourceData.value) return;

    rawFields.value = getRawFields(sourceData.value.rawFields);
    setFieldsSchema();
  }

  function setFieldsSchema() {
    fieldsSchema.value = getUpdatedSchemaModel();
  }

  function setNewSpeciesAdoptionFeeOptions(newValue?: AdoptionFee[], oldValue?: AdoptionFee[]) {
    if (!isAdoption.value) return;

    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'adoption_fee') return;

      const isSameSpecies = isEqual(newValue, oldValue);
      if (isSameSpecies) return;

      const adoptionFeeValue = formData.value?.adoption_fee;
      field.edit ? (field.edit.options = newValue) : null;

      field.model = '';
      field.value = '';

      if (!adoptionFeeValue) return;

      // NOTE :: Match the fee by ID, amount+description, or amount only
      const matchingFee =
        newValue?.find((fee: AdoptionFee) => matchesFeeId(fee._id, adoptionFeeValue)) ??
        newValue?.find(
          (fee: AdoptionFee) =>
            fee.amount === adoptionFeeValue || parseFloat(String(fee.amount)) === parseFloat(adoptionFeeValue)
        );

      const feeId = getFeeIdString(matchingFee?._id);

      field.model = feeId;
      field.value = feeId;
    });
  }

  function setNewSpeciesEuthanasiaFeeOptions(newValue: any, oldValue: any) {
    if (!isEuthanasia.value || !isIntake.value) return;

    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'euthanasia_fee') return;

      const isSameSpecies = isEqual(newValue, oldValue);
      if (isSameSpecies) return;

      const euthanasiaFeeAmount = formData.value?.euthanasia_fee;
      field.edit ? (field.edit.options = newValue) : null;

      field.model = '';
      field.value = '';

      const hasEuthanasiaFee = euthanasiaFeeAmount && euthanasiaFeeAmount !== '0.00';
      if (hasEuthanasiaFee) {
        const matchingFee = newValue?.find(
          (fee: any) => fee.amount === euthanasiaFeeAmount || parseFloat(fee.amount) === parseFloat(euthanasiaFeeAmount)
        );
        const feeId = matchingFee?._id?.$oid || matchingFee?._id || '';
        field.model = feeId;
        field.value = feeId;
      }
    });
  }

  function setWaverField() {
    const waverFields = [
      {
        label: 'Outcome Waivers',
        name: 'waivers',
        required: false,
        type: 'multiselect',
        value: '',
      },
    ];

    fieldsSettings.waivers.edit!.options = waivers?.value;

    const index = rawFields.value.findIndex((field) => field.name === 'rto_fees');
    if (index && index !== -1) return rawFields.value.splice(index + 1, 0, ...waverFields);

    rawFields.value.unshift(...waverFields);
  }

  function setAdoptionFeeField() {
    rawFields.value.unshift({
      label: 'Adoption Fee',
      name: 'adoption_fee',
      required: true,
      type: 'select',
      value: '',
    });
  }

  function setEuthanasiaFeeField() {
    rawFields.value.unshift({
      label: 'Euthanasia Fee',
      name: 'euthanasia_fee',
      required: true,
      type: 'select',
      value: '',
    });
  }

  function setAdditionalFeeField(options?: AdditionalFee[]) {
    if (!options) return;

    const animal = animals.value.find((animal: any) => animal._id === currentDataOnStepperId.value);

    const optionsPerSpecies = options
      .filter((option) => option.species === animal?.species.toLowerCase())
      .sort((a, b) => a.description.localeCompare(b.description));

    if (!optionsPerSpecies.length) return;

    additionalFeesOptions.value = options;

    const newField = {
      label: 'Additional Fee(s)',
      name: 'additional_fees',
      required: false,
      type: 'multiselect',
      value: [],
    };

    const existingIndex = rawFields.value.findIndex((field) => field.name === 'additional_fees');
    existingIndex !== -1 ? (rawFields.value[existingIndex] = newField) : rawFields.value.push(newField);

    const optionsWithCustomLabel = optionsPerSpecies.map((fee: AdditionalFee) => {
      return {
        ...fee,
        label: `${fee.description} - $${fee.amount}`,
      };
    });

    fieldsSettings.additional_fees.edit!.options = optionsWithCustomLabel;
  }

  function setRtoFeesField() {
    const rtoFields = [
      {
        label: 'RTO Fee Type',
        name: 'rto_fee_type',
        required: true,
        type: 'select',
        value: '',
      },
      {
        label: 'RTO Fee(s)',
        name: 'rto_fees',
        required: true,
        disabled: true,
        type: 'select',
        value: '',
      },
    ];

    rawFields.value.unshift(...rtoFields);
  }

  function setDiscountField() {
    if (!discounts.value?.length) return;
    if (!hasCart.value) return;

    const { CLINIC_OUT, SERVICE_OUT, OWNER_REQUESTED_EUTHANASIA } = OUTCOMES_TYPES;
    if ([CLINIC_OUT, SERVICE_OUT, OWNER_REQUESTED_EUTHANASIA].includes(outcomeType.value)) return;

    const adoptionFeeIndex = rawFields.value.findIndex((field: any) => field.name === 'adoption_fee');
    const rtoFeesIndex = rawFields.value.findIndex((field: any) => field.name === 'rto_fees');

    const index = adoptionFeeIndex >= 0 ? adoptionFeeIndex + 1 : rtoFeesIndex + 1;
    const discountField = [
      {
        label: 'Discount',
        name: 'discount',
        required: false,
        type: 'select',
        value: null,
      },
    ];

    rawFields.value.splice(index, 0, ...discountField);
  }

  async function uploadOutcomeTempFile(files: File[]) {
    if (!filesModel.value || !filesModel.value.length) return;

    const animalId = formData.value?.animal_id;
    if (!animalId) return;

    const filesToUpload = files.filter((file: File) => !alreadyUploadedFiles.value.has(`${animalId}-${file.name}`));
    if (!filesToUpload.length) return;

    async function updateFilesModel(file: any) {
      const base64Data = await fileToBase64(file);
      const fileRecord = {
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64Data,
      };

      if (!uploadedFiles.value[animalId]) uploadedFiles.value[animalId] = [];
      uploadedFiles.value[animalId].push(fileRecord);
    }

    const TOAST_ERROR = {
      message: 'cart.toast.outcomeInfoUpdate.error.message',
      title: 'cart.toast.outcomeInfoUpdate.error.title',
      type: TOAST_TYPES.ERROR,
    };

    const TOAST_SUCCESS = {
      title: 'components.fileUploader.toast.title.success',
      type: TOAST_TYPES.SUCCESS,
    };

    const uploadPromises = filesToUpload.map(async (file: File) => {
      return await UploadOutcomeTempFileService(file, animalId).then(async () => {
        alreadyUploadedFiles.value.add(`${animalId}-${file.name}`);
        await updateFilesModel(file);
      });
    });

    await Promise.all(uploadPromises)
      .then(() => notify(TOAST_SUCCESS))
      .catch(() => notify(TOAST_ERROR));
  }

  function onFilesModelUpdate(newValue: any, oldValue: any) {
    if (isStepChanging.value) return;

    const newValueArray = Array.isArray(newValue) ? newValue : [];
    const oldValueArray = Array.isArray(oldValue) ? oldValue : [];

    newValueArray.length > oldValueArray.length
      ? uploadOutcomeTempFile(newValueArray)
      : removeOutcomeTempFile(newValueArray, oldValueArray);
  }

  function removeOutcomeTempFile(newValue: any, oldValue: any) {
    const filesToRemove = oldValue.filter((file: any) => !newValue.includes(file));
    if (!filesToRemove.length) return;

    const animalId = formData.value?.animal_id;
    if (!animalId) return;

    const fileToRemove = filesToRemove[0];
    const payload = {
      animalId,
      filename: fileToRemove.name,
    };

    RemoveOutcomeTempFileService(payload).then(async () => {
      alreadyUploadedFiles.value.delete(`${animalId}-${fileToRemove.name}`);

      if (uploadedFiles.value[animalId]) {
        uploadedFiles.value[animalId] = uploadedFiles.value[animalId].filter(
          (fileRecord: any) => fileRecord.name !== fileToRemove.name
        );
      }
    });
  }

  // GETTERS
  function getUpdatedSchemaModel() {
    const fieldSchema = getDetailsSchema(rawFields.value, fieldsSettings, formData.value);

    fieldSchema.forEach((field) => {
      //prettier-ignore
      switch (field.name) {
        case 'adoption_fee': return setAdoptionFee(field)
        case 'euthanasia_fee': return setEuthanasiaFee(field)
        case 'additional_fees': return setAdditionalFeeField(field);
        case 'waivers': return setWaiversField(field);
        case 'discount': return setDonationField(field);
        default: setMatch(field)
      }
    });

    async function setAdoptionFee(field: Field) {
      const adoptionFeeValue = formData.value?.adoption_fee;

      field.edit ? (field.edit.options = adoptionFeesOptions.value) : null;

      field.model = '';
      field.value = '';

      if (!adoptionFeeValue) return;

      // NOTE :: Match the fee by ID, amount+description, or amount only
      const matchingFee =
        adoptionFeesOptions.value?.find((fee: AdoptionFee) => matchesFeeId(fee._id, adoptionFeeValue)) ??
        adoptionFeesOptions.value?.find(
          (fee: AdoptionFee) =>
            parseFloat(String(fee.amount)) === parseFloat(adoptionFeeValue) &&
            fee.description === formData.value?.fee_name
        ) ??
        adoptionFeesOptions.value?.find(
          (fee: AdoptionFee) => parseFloat(String(fee.amount)) === parseFloat(adoptionFeeValue)
        );

      const feeId = getFeeIdString(matchingFee?._id);

      field.model = feeId;
      field.value = feeId;
    }

    function setEuthanasiaFee(field: Field) {
      const euthanasiaFeeAmount = formData.value?.euthanasia_fee;

      field.edit ? (field.edit.options = euthanasiaFeesOptions.value) : null;

      field.model = '';
      field.value = '';

      const hasEuthanasiaFee = euthanasiaFeeAmount && euthanasiaFeeAmount !== '0.00';
      if (hasEuthanasiaFee) {
        const matchingFee = euthanasiaFeesOptions.value?.find(
          (fee: any) => fee.amount === euthanasiaFeeAmount || parseFloat(fee.amount) === parseFloat(euthanasiaFeeAmount)
        );
        const feeId = matchingFee?._id?.$oid || matchingFee?._id || '';
        field.model = feeId;
        field.value = feeId;
      }
    }

    function setAdditionalFeeField(field: Field) {
      const selectedFees = formData.value?.additional_fees;
      const feesOptions = sourceData.value?.additional_fees_options;
      const missingParameters = !selectedFees || !selectedFees.length || !feesOptions || !feesOptions.length;
      if (missingParameters) return;

      const additionalFees = feesOptions
        .filter((fee: AdditionalFee) => selectedFees.some((selectedFee: any) => selectedFee._id === fee._id))
        .map((fee: AdditionalFee) => {
          return {
            ...fee,
            label: `${fee.description} - $${fee.amount}`,
          };
        });

      field.model = additionalFees;
      field.value = additionalFees;
    }

    function setFileField(field: Field) {
      const storedFiles = uploadedFiles.value[formData.value.animal_id];
      if (!storedFiles) {
        field.model = [];
        field.value = [];
        return;
      }

      // Convert stored base64 data back to File objects with objectURL
      const files = storedFiles.map((fileRecord) => {
        const file = base64ToFile(fileRecord.data, fileRecord.name, fileRecord.type);
        return file;
      });

      field.model = files;
      field.value = files;
    }

    function setDonationField(field: Field) {
      // TODO :: TASK-980 When extending per cart item discount feature, we shoudl review this function
      const activeDiscounts = cartData.value?.active_discounts || [];

      const activeDiscountSelected = !activeDiscounts.length
        ? null
        : discounts.value?.find(
            (discount: any) => discount.code === animals.value[outcomeInfoActiveStep.value]?.discount_code
          );

      field.model = activeDiscountSelected;
      field.value = activeDiscountSelected;
    }

    function setWaiversField(field: Field) {
      if (!formData.value?.waivers) return;

      const selectedIds = formData.value[field.name].map((item: any) => item._id);

      const filteredWaivers = waivers?.value.filter((waiver: any) => selectedIds.includes(waiver._id));
      field.value = filteredWaivers;
      field.model = filteredWaivers;
    }

    function setMatch(field: Field) {
      if (field.settings.type === 'file') {
        setFileField(field);
        return;
      }

      field.value = formData.value[field.name] || formData.value.outcome?.[field.name];
      field.model = formData.value[field.name] || formData.value.outcome?.[field.name];
    }

    return fieldSchema;
  }

  function getRawFields(dynamicFields: RawField[]) {
    const hasAdditionalFeeFields = sourceData.value?.additional_fees_options.length;
    if (hasAdditionalFeeFields && hasCart.value) setAdditionalFeeField(sourceData.value.additional_fees_options);

    const hasPartnerFields = sourceData.value?.partners.length;
    if (hasPartnerFields) fieldsSettings.partner_name.edit!.options = sourceData.value.partners;

    const hasRtoFees = sourceData.value?.rto_fees.length;
    if (hasRtoFees) setRtoFeesField();

    const hasWaiversField =
      isAdoption.value ||
      isReturnToOwner.value ||
      outcomeType.value === OUTCOMES_TYPES.CLINIC_OUT ||
      outcomeType.value === OUTCOMES_TYPES.SERVICE_OUT ||
      outcomeType.value === OUTCOMES_TYPES.TRANSFER_OUT;

    if (hasWaiversField && waivers?.value && waivers?.value?.length) setWaverField();

    if (isAdoption.value) setAdoptionFeeField();

    if (isEuthanasia.value && isIntake.value) setEuthanasiaFeeField();

    const hasDiscounts = discounts.value.length;
    if (hasDiscounts) setDiscountField();

    const sanitizedFields = [...rawFields.value, ...dynamicFields].filter(
      ({ name }) => !NOT_ALLOWED_FIELD.includes(name)
    );

    return sanitizedFields;
  }

  function getRtoFeesUpdatePayload() {
    if (!rtoFeeTypeField.value) return;

    const animalId: undefined | string = formData.value?.animal_id;
    const intakeDate: undefined | string | Moment = formData.value?.intake_date;
    const missingParameters = !animalId || !intakeDate;
    if (missingParameters) return;

    // NOTE : If used somewhere else, this could be promoted to a helper
    const newLos: number = getLOS(moment(formData.value?.intake_date));

    function getLOS(intakeDate: Moment): number {
      const outcomeDateModel = outcomeDateFieldSchema.value[0].model;
      const outcomeDate: Moment = moment(outcomeDateModel || getShelterNow()).startOf('day');
      const losDays: number = outcomeDate.diff(moment(intakeDate).startOf('day'), 'days', true);

      return Math.ceil(losDays + 1);
    }

    const newRtoFee = getRtoFee(newLos);
    function getRtoFee(los: number) {
      if (!rtoFeeField.value) return '';

      const rtoIsDaily = rtoFeeTypeField.value === 'daily';
      const amount = Math.round(parseFloat(rtoFeeField.value.amount) * 100) / 100;

      return rtoIsDaily ? amount * los : amount;
    }

    return {
      base_rto_fee: rtoFeeField.value?.amount ?? '',
      id: animalId,
      newLos,
      newRtoFee,
      rto_fee_type: rtoFeeTypeField.value,
      fee_name: rtoFeeField.value?.description,
    };
  }

  // HELPERS
  function updateAnimalFieldsOnStepUpdate() {
    const currentAnimalIndex = animals.value.findIndex((animal: any) => animal._id === currentDataOnStepperId.value);

    const currentAnimalModels = outcomeSchemaModels.value?.length
      ? outcomeSchemaModels.value[currentAnimalIndex]
      : null;

    const currentOutcomeFields = currentAnimalModels ? getSanitizedPayload(currentAnimalModels) : null;

    // NOTE :: Discount is handled by the onDiscountUpdate function
    delete currentOutcomeFields?.discount;

    // NOTE :: Shallow update for the target animal
    cartData.value.animals[currentAnimalIndex] = {
      ...cartData.value.animals[currentAnimalIndex],
      ...currentOutcomeFields,
    };
  }

  // EVENTS
  function onWaiversUpdate() {
    hasWaivers.value = !!(waiversModel.value && waiversModel.value.length);
  }

  function onStepUpdate() {
    isStepChanging.value = true;
    updateAnimalFieldsOnStepUpdate();
    fieldsSchema.value = [];
    setFieldsSchema();
    onRtoFeeTypeUpdate();

    setTimeout(() => {
      isStepChanging.value = false;
    }, 100);
  }

  function onRtoFeeTypeUpdate() {
    if (!rtoFeeTypeField.value) return;

    const rtoFields = sourceData.value?.rto_fees;
    if (!rtoFields.length) return;

    updateRtoFeeOptions();

    function updateRtoFeeOptions() {
      const rtoFeesField = fieldsSchema.value.find(({ name }) => name === 'rto_fees');
      if (!rtoFeesField) return;

      if (!rtoFeesField?.edit) return;
      const animal = animals.value.find((animal: any) => animal._id === currentDataOnStepperId.value);

      const options: RtoFee[] = rtoFields
        .filter(
          (fee: any) =>
            fee.type === rtoFeeTypeField.value.toLowerCase() &&
            fee.species.toLowerCase() === animal.species.toLowerCase()
        )
        .map((fee: RtoFee) => {
          return {
            ...fee,
            label: `${fee.description} - $${fee.amount}`,
          };
        });

      rtoFeesField.state.disabled = false;
      rtoFeesField.edit.options = options;
      rtoFeesField.settings.placeholder = 'Select a RTO Fee';

      const rtoFee = getSelectedRtoFee();
      function getSelectedRtoFee() {
        if (selectedRtoFee.value[outcomeInfoActiveStep.value]) {
          return selectedRtoFee.value[outcomeInfoActiveStep.value];
        }

        const rtoFee = formData.value?.base_rto_fee || parseFloat(formData.value.base_rto_fee);

        if (!rtoFee) return undefined;

        const selectedRtoFeeData = rtoFees.value
          .filter((fee: any) => fee.type === rtoFeeTypeField.value.toLowerCase())
          .find(({ amount }: RtoFee) => parseFloat(rtoFee) === parseFloat(String(amount)));

        if (!selectedRtoFeeData) return undefined;

        return {
          ...selectedRtoFeeData,
          label: `${selectedRtoFeeData.description} - $${selectedRtoFeeData.amount}`,
        };
      }

      // NOTE: This setTimeout is necessary so the cart summary hava time to update its values when
      // going back and forth in the outcome information page
      setTimeout(() => {
        rtoFeesField.model = rtoFee;
        rtoFeesField.value = rtoFee;
      }, 50);
    }

    // NOTE : If we start to store all fee information within the outcome this should be reviewed
    const noUpdate = formData.value.rto_fee_type === rtoFeeTypeField.value;
    if (noUpdate) return;

    const PAYLOAD = getRtoFeesUpdatePayload();
    if (!PAYLOAD) return;

    PAYLOAD.base_rto_fee = '';

    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'rto_fees') return;

      field.model = '';
      field.value = '';
      field.state.errors = new Set();
    });

    UpdateRtoFeeService(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  function onDiscountUpdate(newDiscount: any, oldDiscount: any) {
    const isRemovingDiscount = oldDiscount && !newDiscount;
    if (isRemovingDiscount) {
      const PAYLOAD = {
        code: oldDiscount.code,
        item_id: animals.value[outcomeInfoActiveStep.value]._id,
        target_item: 'animals',
      };

      return DeleteItemDiscount(PAYLOAD)
        .then(() => useCartStore().fetchActiveCart())
        .catch(() => notify(TOAST_ERROR));
    }

    if (Number(newDiscount) <= 0 && !oldDiscount) return;

    // TODO :: TASK-980 When extending per cart item discount feature, we shoudl review this function
    const PAYLOAD = {
      code: discountField.value.code,
      item_id: animals.value[outcomeInfoActiveStep.value]._id,
      target_item: 'animals',
    };

    AddDiscountItemService(PAYLOAD)
      .then(() => useCartStore().fetchActiveCart())
      .catch(() => notify(TOAST_ERROR));
  }

  function onOutcomeDateUpdate() {
    const PAYLOAD = getRtoFeesUpdatePayload();
    if (!PAYLOAD) return;

    UpdateRtoFeeService(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  function onRtoFeesUpdate() {
    // NOTE : If we start to store all fee information within the outcome this should be reviewed
    const noUpdate = parseFloat(formData.value.base_rto_fee) === parseFloat(rtoFeeField.value?.amount);
    if (noUpdate) return;

    const PAYLOAD = getRtoFeesUpdatePayload();
    if (!PAYLOAD) return;

    // NOTE: We use this selectedRtoFee state to repopulate the select RTO Fees field after hard refresh
    if (rtoFeeField.value) {
      selectedRtoFee.value[outcomeInfoActiveStep.value] = {
        ...rtoFeeField.value,
        label: `${rtoFeeField.value.description} - $${rtoFeeField.value.amount}`,
      };
    }

    UpdateRtoFeeService(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  function onAdoptionFeeUpdate() {
    const adoptionFeeId: string = adoptionFee.value;
    if (!adoptionFeeId) return;

    const animalId: undefined | string = formData.value?.animal_id;
    if (!animalId) return;

    const selectedFee = adoptionFeesOptions.value?.find((fee: AdoptionFee) => matchesFeeId(fee._id, adoptionFeeId));

    const adoptionFeeAmount = String(selectedFee?.amount ?? '0.00');

    const PAYLOAD = {
      adoption_fee: adoptionFeeAmount,
      animal_id: animalId,
      fee_name: selectedFee?.description,
    };

    UpdateAnimalCartAdoptionFee(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  function onEuthanasiaFeeUpdate() {
    const euthanasiaFeeId: string = euthanasiaFee.value;
    if (!euthanasiaFeeId) return;

    const animalId: undefined | string = formData.value?.animal_id;
    if (!animalId) return;

    const selectedFee = euthanasiaFeesOptions.value?.find(
      (fee: any) => fee._id?.$oid === euthanasiaFeeId || fee._id === euthanasiaFeeId
    );

    const euthanasiaFeeAmount = selectedFee?.amount || '0.00';

    const PAYLOAD = {
      euthanasia_fee: euthanasiaFeeAmount,
      animal_id: animalId,
      fee_name: selectedFee?.description,
    };

    UpdateAnimalCartEuthanasiaFeeService(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  function handleAdditionalFeesChange(
    newValue: { model: AdditionalFee[]; blur: boolean },
    oldValue?: { model: AdditionalFee[]; blur: boolean }
  ) {
    let blurTimeout: ReturnType<typeof setTimeout> | null = null;

    if (!newValue.blur) return;

    if (blurTimeout) clearTimeout(blurTimeout);
    blurTimeout = setTimeout(() => {
      onAdditionalFeesUpdate(oldValue?.model);
    }, 150);
  }

  function onAdditionalFeesUpdate(oldFees?: AdditionalFee[]) {
    if (!additionalFeesBlurState.value) return;

    const isInitalFormLoad = typeof oldFees === 'undefined';
    if (isInitalFormLoad) return;

    const additionalFee: AdditionalFee[] = fieldsSchema.value.find(({ name }) => name === 'additional_fees')?.model;
    const animalId: undefined | string = formData.value?.animal_id;

    if (!animalId) return;

    const PAYLOAD = {
      animals: {
        additional_fees: additionalFee,
        animal_id: animalId,
        outcome_field: true,
      },
    };

    UpdateAdditionalFeesService(PAYLOAD)
      .then(({ data }) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  return {
    additionalFeesOptions,
    adoptionFeesOptions,
    currentDataOnStepperId,
    currentSchemaModels,
    euthanasiaFeesOptions,
    fieldsSchema,
    formError,
    outcomeSchemaModels,
    rawFields,
    sourceData,
    waivers,

    setForm,
    setAdditionalFeeField,
  };
}
